import { createFileRoute, useParams, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Code2 } from 'lucide-react';
import { getErrorMessage } from '@weebee/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { JsonEditor } from '@/components/ui/json-editor';
import { KvDiff } from '@/components/ui/kv-diff';
import { ErrorState } from '@/components/state/error-state';
import { queryKeys } from '@/lib/query-keys';
import { formatRelative } from '@/lib/format';
import { getConfig, updateConfig, resetConfig } from '@/features/platform-config/api';
import {
  resolveDescriptor,
  validateConfig,
  type JsonSchema,
} from '@/features/platform-config/descriptors';
import { ConfigForm } from '@/features/platform-config/components/config-form';

export const Route = createFileRoute('/_authenticated/platform-config/$namespace')({
  component: ConfigDetailPage,
});

type Cfg = Record<string, unknown>;
const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function ConfigDetailPage() {
  const { namespace } = useParams({ from: '/_authenticated/platform-config/$namespace' });
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: queryKeys.platformConfig.namespace(namespace),
    queryFn: () => getConfig(namespace),
  });
  const config = detail.data;
  const loaded = useMemo(() => (config?.config ?? {}) as Cfg, [config?.config]);
  const defaults = useMemo(() => (config?.defaults ?? {}) as Cfg, [config?.defaults]);

  // Prefer the backend-emitted JSON Schema (authoritative types/ranges/enums);
  // resolveDescriptor merges it with the TS descriptor's labels/grouping and
  // falls back to the TS descriptor alone when no schema is present.
  const schema = config?.configSchema as JsonSchema | undefined;
  const descriptor = useMemo(() => resolveDescriptor(namespace, schema), [namespace, schema]);

  // Structured (form) state + raw-JSON state. Structured is default when a
  // descriptor exists; raw is forced for unknown namespaces.
  const [values, setValues] = useState<Cfg>({});
  // Default to raw only when there's no TS descriptor (schema, if any, arrives
  // with the config and won't have loaded on first render).
  const [raw, setRaw] = useState(() => resolveDescriptor(namespace, undefined) === undefined);
  const [rawText, setRawText] = useState('');
  const [rawError, setRawError] = useState<string | null>(null);
  const [review, setReview] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // Seed editors when the config loads / refetches (keyed on etag).
  useEffect(() => {
    if (!config) return;
    setValues(config.config as Cfg);
    setRawText(JSON.stringify(config.config, null, 2));
    setRawError(null);
  }, [config?.etag]); // eslint-disable-line react-hooks/exhaustive-deps

  // The payload the current mode would submit (complete object — partial
  // overrides are rejected by the backend validators).
  const payload: Cfg | null = useMemo(() => {
    if (!raw) return values;
    try {
      return JSON.parse(rawText) as Cfg;
    } catch {
      return null;
    }
  }, [raw, values, rawText]);

  const fieldErrors = useMemo(
    () => (descriptor && !raw ? validateConfig(descriptor, values) : {}),
    [descriptor, raw, values]
  );
  const hasErrors = raw ? rawError !== null : Object.keys(fieldErrors).length > 0;
  const dirty = payload != null && !eq(payload, loaded);
  const changedCount = useMemo(() => {
    if (!payload) return 0;
    const keys = new Set([...Object.keys(loaded), ...Object.keys(payload)]);
    return [...keys].filter((k) => !eq(loaded[k], payload[k])).length;
  }, [payload, loaded]);

  const save = useMutation({
    mutationFn: () => {
      if (!payload) throw new Error('Nothing to save');
      return updateConfig(namespace, payload, config?.etag);
    },
    onSuccess: async () => {
      setReview(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.platformConfig.namespace(namespace) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.platformConfig.list() }),
      ]);
    },
  });

  const reset = useMutation({
    mutationFn: () => resetConfig(namespace),
    onSuccess: async () => {
      setConfirmReset(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.platformConfig.namespace(namespace) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.platformConfig.list() }),
      ]);
    },
  });

  function onRawChange(text: string) {
    setRawText(text);
    try {
      JSON.parse(text);
      setRawError(null);
    } catch (e) {
      setRawError((e as Error).message);
    }
  }

  function toggleRaw() {
    if (!raw) {
      // structured → raw: seed text from current edits
      setRawText(JSON.stringify(values, null, 2));
      setRawError(null);
      setRaw(true);
    } else if (descriptor) {
      // raw → structured: adopt parsed edits if valid
      try {
        setValues(JSON.parse(rawText) as Cfg);
        setRaw(false);
      } catch {
        setRawError('Fix the JSON before switching back to the form.');
      }
    }
  }

  function discard() {
    setValues(config?.config as Cfg);
    setRawText(JSON.stringify(config?.config, null, 2));
    setRawError(null);
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Link
        to="/platform-config"
        className="wb-small"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}
      >
        <ArrowLeft size={15} /> Back to config
      </Link>

      {detail.isLoading ? (
        <span className="wb-skeleton" style={{ width: 240, height: 28, display: 'block' }} />
      ) : detail.error ? (
        <ErrorState error={detail.error} onRetry={() => detail.refetch()} />
      ) : config ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <h1 className="wb-h1 wb-mono">{config.namespace}</h1>
            <Badge tone={config.has_override ? 'honey' : 'neutral'}>
              {config.has_override ? 'Override active' : 'Using defaults'}
            </Badge>
            <Badge tone="neutral">v{config.version}</Badge>
          </div>
          <p className="wb-sub" style={{ marginBottom: 20 }}>
            {config.description}
            {config.updated_by
              ? ` · last changed by ${config.updated_by} ${config.updated_at ? formatRelative(config.updated_at) : ''}`
              : ''}
          </p>

          <Card>
            <CardContent>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span className="wb-eyebrow">Configuration</span>
                {descriptor ? (
                  <button
                    type="button"
                    className="wb-fld-reset"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    onClick={toggleRaw}
                  >
                    <Code2 size={13} /> {raw ? 'Form view' : 'Advanced (raw JSON)'}
                  </button>
                ) : null}
              </div>

              {descriptor && !raw ? (
                <ConfigForm
                  descriptor={descriptor}
                  values={values}
                  defaults={defaults}
                  errors={fieldErrors}
                  onChange={setValues}
                />
              ) : (
                <JsonEditor value={rawText} onChange={onRawChange} error={rawError} rows={18} />
              )}

              {save.isError ? (
                <div className="wb-form-error">{getErrorMessage(save.error)}</div>
              ) : null}

              <div style={{ display: 'flex', marginTop: 16 }}>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setConfirmReset(true)}
                  disabled={!config.has_override || save.isPending}
                >
                  Reset to defaults
                </Button>
              </div>
            </CardContent>
          </Card>

          {dirty ? (
            <div className="wb-actionbar">
              <span className="wb-actionbar-msg">
                {changedCount} unsaved {changedCount === 1 ? 'change' : 'changes'}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <Button size="sm" variant="ghost" onClick={discard} disabled={save.isPending}>
                  Discard
                </Button>
                <Button size="sm" onClick={() => setReview(true)} disabled={hasErrors}>
                  Review &amp; save →
                </Button>
              </div>
            </div>
          ) : null}

          <Modal
            open={review}
            onClose={() => setReview(false)}
            title="Review changes"
            footer={
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReview(false)}
                  disabled={save.isPending}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save config'}
                </Button>
              </>
            }
          >
            <p className="wb-small" style={{ marginBottom: 12 }}>
              These changes apply fleet-wide within ~5 minutes.
            </p>
            <KvDiff before={loaded} after={payload ?? undefined} />
            {save.isError ? (
              <div className="wb-form-error">{getErrorMessage(save.error)}</div>
            ) : null}
          </Modal>

          <Modal
            open={confirmReset}
            onClose={() => setConfirmReset(false)}
            title="Reset to defaults?"
            footer={
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmReset(false)}
                  disabled={reset.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => reset.mutate()}
                  disabled={reset.isPending}
                >
                  {reset.isPending ? 'Resetting…' : 'Reset'}
                </Button>
              </>
            }
          >
            <p className="wb-small">
              This clears the override for <span className="wb-mono">{config.namespace}</span> and
              restores code defaults across the fleet. This cannot be undone.
            </p>
            {reset.isError ? (
              <div className="wb-form-error">{getErrorMessage(reset.error)}</div>
            ) : null}
          </Modal>
        </>
      ) : null}
    </div>
  );
}
